<template>
	<div>
		<v-container fluid class="pa-0">
			<!-- Billing interval toggle -->
			<v-row dense justify="center" class="mb-2">
				<v-btn-toggle v-model="priceToggle" mandatory dense rounded color="primary" :class="{ loading: inProgress }" @change="selectPlan(undefined, true)">
					<v-btn :value="false" small class="px-4">Monthly</v-btn>
					<v-btn :value="true" small class="px-4">
						Annual
						<v-chip x-small color="green" text-color="white" class="ml-2 px-2">Save 20%</v-chip>
					</v-btn>
				</v-btn-toggle>
			</v-row>

			<v-row dense justify="center" align="stretch">
				<v-col
					v-for="card in subscriptionPlanOptions" :key="card.title" cols="12" sm="4" class="plan-card d-flex"
					:class="{ active: isSelected(card.title), loading: inProgress }">
					<v-card outlined class="flex-grow-1 d-flex flex-column plan-card-inner" :ripple="!inProgress" @click="selectPlan(card.title, false)">
						<v-card-title class="pb-0 pt-3 plan-title">
							<span>{{ card.title }}</span>
							<v-spacer></v-spacer>
							<v-icon v-if="isSelected(card.title)" color="primary" small>fas fa-check-circle</v-icon>
						</v-card-title>
						<v-card-text class="pb-3">
							<div class="mt-1 mb-2">
								<span class="plan-price">{{ priceToggle ? card.price.yearly : card.price.monthly }}</span>
								<span class="plan-price-period">/ month</span>
							</div>
							<div class="caption grey--text text--darken-1 mb-3">
								{{ priceToggle ? "Billed annually" : "Billed monthly" }}
							</div>
							<v-divider class="mb-3"></v-divider>
							<div v-for="feature in card.features" :key="feature" class="d-flex align-center mb-2">
								<v-icon x-small color="primary" class="mr-2">fas fa-check</v-icon>
								<span class="body-2">{{ feature }}</span>
							</div>
						</v-card-text>
					</v-card>
				</v-col>

				<!-- Enterprise: sales-assisted, no listed price -->
				<v-col cols="12" sm="4" class="plan-card d-flex" :class="{ loading: inProgress }">
					<v-card outlined class="flex-grow-1 d-flex flex-column plan-card-inner" :href="enterpriseContactUrl">
						<v-card-title class="pb-0 pt-3 plan-title">
							<span>Enterprise</span>
						</v-card-title>
						<v-card-text class="pb-3">
							<div class="mt-1 mb-2">
								<span class="plan-price">Custom</span>
							</div>
							<div class="caption grey--text text--darken-1 mb-3">
								Contact us for pricing
							</div>
							<v-divider class="mb-3"></v-divider>
							<div v-for="feature in enterpriseFeatures" :key="feature" class="d-flex align-center mb-2">
								<v-icon x-small color="primary" class="mr-2">fas fa-check</v-icon>
								<span class="body-2">{{ feature }}</span>
							</div>
							<v-btn small outlined color="primary" class="mt-2" :href="enterpriseContactUrl">
								<v-icon x-small left>fas fa-envelope</v-icon>
								Contact Us
							</v-btn>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
	import {VueController} from "@/service/support/vuex-controller";

	export default VueController("PlanSelection", {
		state: [
			"newOrganizationTier",
			"organization",
      "subscriptionPlanOptions",
			"validPromoCode",
        "stripeInterval"
		],
		data(){
		    return {
		        localSelectedPlan : "",
				    priceToggle: false,
          inProgress: false,
          enterpriseFeatures: ["Unlimited Users", "Unlimited Client Portals", "Unlimited Storage", "API Access & Webhooks"],
          enterpriseContactUrl: "mailto:support@zapaportal.com?subject=Enterprise Plan Inquiry"
			}
		},
    props: {
      defaultPlan: {
        type: String,
        default: ''
      }
    },
    computed: {
      currentPlan() {
        let currentPlanString = "";
        switch (this.localSelectedPlan) {
          case "large":
          case "large_firm":
          case "large firm":
          case "Large Firm":
          case "advanced":
          case "Advanced":
            currentPlanString = "Large Firm";
            break;
          case "enterprise":
          case "Enterprise":
            currentPlanString = "Enterprise";
            break;
          case "pro":
          case "professional":
          case "Professional":
            currentPlanString = "Professional";
            break;
        }
        return currentPlanString;
      }
    },
		mounted (){
			this.refresh();
		},
    methods: {
      isSelected(planTitle) {
        return planTitle === this.currentPlan && (this.priceToggle === (this.stripeInterval === 'year'));
      },
      setInProgress(value) {
        this.inProgress = value;
      },
      setDiscount(discount) {
        this.priceToggle = discount;
      },
      updateNoPlan() {
        this.localSelectedPlan = "";
        this.priceToggle = false;
      },
      refresh() {
        this.validPromoCode = '';
        this.priceToggle = this.stripeInterval.toLowerCase() !== "month";

        //console.log("Local plan is " + this.localSelectedPlan);
        if (this.defaultPlan === "none") {
          this.localSelectedPlan = "";
        } else if (this.defaultPlan) {
          this.localSelectedPlan = this.defaultPlan;
        } else {
          if (this.organization && this.organization.tier !== 'null') {
            this.localSelectedPlan = this.organization.tier;
          } else {
            this.localSelectedPlan = this.newOrganizationTier;
          }
          if (!this.localSelectedPlan) {
            this.localSelectedPlan = "Professional";
          }
        }
      },
			selectPlan(planName, onlyDiscount) {
        if (!this.inProgress) {
          if (planName) {
            this.localSelectedPlan = planName;
          }

          this.update("stripeDiscount", this.priceToggle);
          this.update("newOrganizationTier",  this.localSelectedPlan.toLowerCase());
          //console.log("Discount = " + this.priceToggle + " SelectedPlan = " + this.localSelectedPlan);
          if (!onlyDiscount) {
            this.$emit('newPlanSelected', {
              plan: this.localSelectedPlan, discount: this.priceToggle, onlyDiscount
            });
          }
        }
			}
		}
	});
</script>

<style lang="scss" scoped>
	.plan-card-inner {
		cursor: pointer;
		transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
		border-width: 2px;

		&:hover {
			box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
		}
	}

	.plan-card.active .plan-card-inner {
		border: 2px solid #0D7542;
	}

	.plan-title {
		font-size: 1.1rem;
		font-weight: 600;
		width: 100%;
	}

	.plan-price {
		font-size: 1.9rem;
		font-weight: 700;
		line-height: 1.2;
	}

	.plan-price-period {
		font-size: .9rem;
		color: rgba(0, 0, 0, .6);
		margin-left: 2px;
	}

	.loading {
		opacity: .4;
		pointer-events: none;
	}
</style>
